import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchLessons, deleteLesson } from '../api/lessons';

const LessonsPage = () => {
    const [lessons, setLessons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [selectedLessonIds, setSelectedLessonIds] = useState(new Set());

    const loadLessons = async () => {
        setLoading(true);
        try {
            const data = await fetchLessons();
            const lessonsArray = Array.isArray(data) ? data : (data.data || []);
            setLessons(lessonsArray);
        } catch (err) {
            setError('Failed to load lessons');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadLessons();
    }, []);

    const toggleSelectionMode = () => {
        setIsSelectionMode(!isSelectionMode);
        setSelectedLessonIds(new Set());
    };

    const toggleLessonSelection = (id) => {
        const newSelected = new Set(selectedLessonIds);
        if (newSelected.has(id)) {
            newSelected.delete(id);
        } else {
            newSelected.add(id);
        }
        setSelectedLessonIds(newSelected);
    };

    const deleteSelectedLessons = async () => {
        if (selectedLessonIds.size === 0) return;

        if (!window.confirm(`Are you sure you want to delete ${selectedLessonIds.size} lesson${selectedLessonIds.size > 1 ? 's' : ''}?`)) {
            return;
        }

        try {
            await Promise.all(Array.from(selectedLessonIds).map(id => deleteLesson(id)));
            await loadLessons();
            setIsSelectionMode(false);
            setSelectedLessonIds(new Set());
        } catch (err) {
            console.error('Failed to delete lessons:', err);
            alert('Failed to delete some lessons. Please try again.');
        }
    };

    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <div style={styles.loadingSpinner}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--primary-500)" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                        <path d="M12 2a10 10 0 0 1 10 10" style={{ animation: 'spin 1s linear infinite', transformOrigin: 'center' }} />
                    </svg>
                </div>
                <p style={styles.loadingText}>Loading your lessons...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div style={styles.container}>
                <div style={styles.errorContainer}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--danger-500)" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <h2 style={styles.errorTitle}>Something went wrong</h2>
                    <p style={styles.errorText}>{error}</p>
                    <button style={styles.retryButton} onClick={loadLessons}>
                        Try again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div style={styles.headerLeft}>
                    <h1 style={styles.title}>My Lessons</h1>
                    <p style={styles.subtitle}>{lessons.length} lesson{lessons.length !== 1 ? 's' : ''}</p>
                </div>
                <div style={styles.headerRight}>
                    {isSelectionMode && selectedLessonIds.size > 0 && (
                        <button style={styles.deleteButton} onClick={deleteSelectedLessons}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            </svg>
                            Delete ({selectedLessonIds.size})
                        </button>
                    )}
                    <button
                        style={isSelectionMode ? styles.cancelButton : styles.selectButton}
                        onClick={toggleSelectionMode}
                    >
                        {isSelectionMode ? 'Cancel' : 'Select'}
                    </button>
                    <Link to="/lessons/new" style={styles.createButton}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="12" y1="5" x2="12" y2="19" />
                            <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                        Create New
                    </Link>
                </div>
            </div>

            {lessons.length === 0 ? (
                <div style={styles.emptyState}>
                    <div style={styles.emptyIcon}>
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="var(--gray-300)" strokeWidth="1.5">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                            <line x1="16" y1="13" x2="8" y2="13" />
                            <line x1="16" y1="17" x2="8" y2="17" />
                        </svg>
                    </div>
                    <h2 style={styles.emptyTitle}>No lessons yet</h2>
                    <p style={styles.emptyText}>
                        Start by creating your first lesson plan with our AI assistant.
                    </p>
                    <Link to="/lessons/new" style={styles.emptyButton}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                        Create Your First Lesson
                    </Link>
                </div>
            ) : (
                <div style={styles.grid}>
                    {lessons.map((lesson) => {
                        const isSelected = selectedLessonIds.has(lesson.id);
                        // Support both old (lesson.title) and new (lesson.sections.title) data shapes
                        const title = lesson.sections?.title || lesson.title;
                        const subject = lesson.sections?.subject || lesson.subject;
                        const duration = lesson.sections?.duration || lesson.duration;
                        const gradeLevel = lesson.sections?.grade_level || lesson.sections?.gradeLevel || lesson.gradeLevel;
                        const topic = lesson.sections?.topic || lesson.topic;

                        const cardContent = (
                            <div
                                style={{
                                    ...styles.card,
                                    ...(isSelected ? styles.selectedCard : {})
                                }}
                            >
                                {isSelectionMode && (
                                    <div style={styles.checkbox}>
                                        <div style={{
                                            ...styles.checkboxInner,
                                            ...(isSelected ? styles.checkboxChecked : {})
                                        }}>
                                            {isSelected && (
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                                                    <polyline points="20 6 9 17 4 12" />
                                                </svg>
                                            )}
                                        </div>
                                    </div>
                                )}
                                <div style={styles.cardIcon}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                        <polyline points="14 2 14 8 20 8" />
                                    </svg>
                                </div>
                                <h2 style={styles.cardTitle}>{title}</h2>
                                <div style={styles.cardMeta}>
                                    <span style={styles.metaTag}>
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                                            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                                        </svg>
                                        {subject}
                                    </span>
                                    <span style={styles.metaDot}></span>
                                    <span style={styles.metaTag}>
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <circle cx="12" cy="12" r="10" />
                                            <polyline points="12 6 12 12 16 14" />
                                        </svg>
                                        {duration} min
                                    </span>
                                    <span style={styles.metaDot}></span>
                                    <span style={styles.metaTag}>Grade {gradeLevel}</span>
                                </div>
                                {topic && (
                                    <p style={styles.cardTopic}>{topic}</p>
                                )}
                            </div>
                        );

                        return isSelectionMode ? (
                            <div
                                key={lesson.id}
                                onClick={() => toggleLessonSelection(lesson.id)}
                                style={{ cursor: 'pointer' }}
                            >
                                {cardContent}
                            </div>
                        ) : (
                            <Link
                                to={`/lessons/${lesson.id}`}
                                key={lesson.id}
                                style={{ textDecoration: 'none', color: 'inherit' }}
                            >
                                {cardContent}
                            </Link>
                        );
                    })}
                </div>
            )}

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

const styles = {
    container: {
        maxWidth: '1000px',
        margin: '0 auto',
        padding: '32px 24px',
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        gap: '16px',
    },
    loadingSpinner: {
        animation: 'spin 1s linear infinite',
    },
    loadingText: {
        color: 'var(--gray-500)',
        fontSize: '15px',
    },
    errorContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        textAlign: 'center',
        gap: '16px',
    },
    errorTitle: {
        margin: 0,
        fontSize: '24px',
        fontWeight: '600',
        color: 'var(--gray-900)',
    },
    errorText: {
        margin: 0,
        color: 'var(--gray-500)',
    },
    retryButton: {
        padding: '10px 20px',
        backgroundColor: 'var(--primary-600)',
        color: 'white',
        border: 'none',
        borderRadius: 'var(--radius-md)',
        fontSize: '14px',
        fontWeight: '500',
        cursor: 'pointer',
        marginTop: '8px',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '32px',
        flexWrap: 'wrap',
        gap: '16px',
    },
    headerLeft: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    title: {
        margin: 0,
        fontSize: '28px',
        fontWeight: '700',
        color: 'var(--gray-900)',
    },
    subtitle: {
        margin: 0,
        fontSize: '14px',
        color: 'var(--gray-500)',
    },
    headerRight: {
        display: 'flex',
        gap: '10px',
        alignItems: 'center',
    },
    selectButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '10px 16px',
        backgroundColor: 'white',
        color: 'var(--gray-700)',
        border: '1px solid var(--gray-300)',
        borderRadius: 'var(--radius-md)',
        fontSize: '14px',
        fontWeight: '500',
        cursor: 'pointer',
        transition: 'all var(--transition-fast)',
    },
    cancelButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '10px 16px',
        backgroundColor: 'var(--gray-100)',
        color: 'var(--gray-700)',
        border: '1px solid var(--gray-200)',
        borderRadius: 'var(--radius-md)',
        fontSize: '14px',
        fontWeight: '500',
        cursor: 'pointer',
    },
    deleteButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '10px 16px',
        backgroundColor: 'var(--danger-600)',
        color: 'white',
        border: 'none',
        borderRadius: 'var(--radius-md)',
        fontSize: '14px',
        fontWeight: '500',
        cursor: 'pointer',
    },
    createButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '10px 20px',
        background: 'linear-gradient(135deg, var(--primary-500) 0%, var(--primary-600) 100%)',
        color: 'white',
        textDecoration: 'none',
        borderRadius: 'var(--radius-md)',
        fontSize: '14px',
        fontWeight: '600',
        boxShadow: '0 2px 4px rgba(37, 99, 235, 0.2)',
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '60px 20px',
        backgroundColor: 'white',
        borderRadius: 'var(--radius-xl)',
        border: '1px solid var(--gray-200)',
    },
    emptyIcon: {
        marginBottom: '20px',
    },
    emptyTitle: {
        margin: '0 0 8px 0',
        fontSize: '20px',
        fontWeight: '600',
        color: 'var(--gray-900)',
    },
    emptyText: {
        margin: '0 0 24px 0',
        color: 'var(--gray-500)',
        maxWidth: '300px',
    },
    emptyButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 24px',
        background: 'linear-gradient(135deg, var(--primary-500) 0%, var(--primary-600) 100%)',
        color: 'white',
        textDecoration: 'none',
        borderRadius: 'var(--radius-md)',
        fontSize: '15px',
        fontWeight: '600',
        boxShadow: '0 2px 4px rgba(37, 99, 235, 0.2)',
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: '20px',
    },
    card: {
        position: 'relative',
        padding: '24px',
        backgroundColor: 'white',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--gray-200)',
        transition: 'all var(--transition-fast)',
        boxShadow: 'var(--shadow-sm)',
    },
    selectedCard: {
        borderColor: 'var(--primary-500)',
        backgroundColor: 'var(--primary-50)',
        boxShadow: '0 0 0 2px var(--primary-100)',
    },
    checkbox: {
        position: 'absolute',
        top: '16px',
        right: '16px',
    },
    checkboxInner: {
        width: '22px',
        height: '22px',
        borderRadius: 'var(--radius-sm)',
        border: '2px solid var(--gray-300)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'white',
        transition: 'all var(--transition-fast)',
    },
    checkboxChecked: {
        backgroundColor: 'var(--primary-600)',
        borderColor: 'var(--primary-600)',
    },
    cardIcon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '40px',
        height: '40px',
        borderRadius: 'var(--radius-md)',
        backgroundColor: 'var(--primary-50)',
        color: 'var(--primary-600)',
        marginBottom: '16px',
    },
    cardTitle: {
        margin: '0 0 12px 0',
        fontSize: '18px',
        fontWeight: '600',
        color: 'var(--gray-900)',
        paddingRight: '30px',
    },
    cardMeta: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'wrap',
        marginBottom: '12px',
    },
    metaTag: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '13px',
        color: 'var(--gray-500)',
    },
    metaDot: {
        width: '3px',
        height: '3px',
        borderRadius: '50%',
        backgroundColor: 'var(--gray-300)',
    },
    cardTopic: {
        margin: 0,
        fontSize: '14px',
        color: 'var(--gray-600)',
        lineHeight: '1.5',
    },
};

export default LessonsPage;
